import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Chip,
  Skeleton,
  Divider,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { userProjectService } from '../../services/userProjectService';
import { useAuth } from '../../context/AuthContext';
import RoleBadge from '../Common/RoleBadge';
import SubordinateProjectAssignmentDialog from './SubordinateProjectAssignmentDialog';

/**
 * Team Management Component
 * For SuperUsers to manage their subordinates and assign projects
 */
const TeamManagement = () => {
  const { user: currentUser } = useAuth();
  const [subordinates, setSubordinates] = useState([]);
  const [myProjects, setMyProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog state
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [selectedSubordinate, setSelectedSubordinate] = useState(null);

  useEffect(() => {
    if (currentUser) {
      loadData();
    }
  }, [currentUser]);

  const loadData = async () => {
    if (!currentUser) return;

    setLoading(true);
    try {
      // Load subordinates and my projects
      const [subs, projects] = await Promise.all([
        userProjectService.getMySubordinates(),
        userProjectService.getUserProjects(currentUser.user_id),
      ]);

      setSubordinates(subs);
      setMyProjects(projects);
    } catch (err) {
      setError('Failed to load team data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignProjects = (subordinate) => {
    setSelectedSubordinate(subordinate);
    setOpenAssignDialog(true);
  };

  const handleDialogClose = (shouldReload) => {
    setOpenAssignDialog(false);
    setSelectedSubordinate(null);
    if (shouldReload) {
      loadData();
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 32 }} />
          My Team
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage your subordinates and assign them to your projects
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* My Projects Summary */}
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Your Assigned Projects ({myProjects.length})
          </Typography>
          {myProjects.length === 0 ? (
            <Alert severity="info">
              You have no projects assigned. Contact your administrator to get projects assigned.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {myProjects.map((proj) => (
                <Chip
                  key={proj.project_code}
                  label={`${proj.project_code} - ${proj.project?.name || 'Project'}`}
                  variant="outlined"
                  color="primary"
                  size="small"
                />
              ))}
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            You can assign these projects to your team members
          </Typography>
        </CardContent>
      </Card>

      {/* Subordinates List */}
      <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Team Members ({subordinates.length})
            </Typography>
          </Box>

          {subordinates.length === 0 ? (
            <Alert severity="info">
              You don't have any team members assigned yet. Contact your administrator to have users
              assigned to you as their manager.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                You manage {subordinates.length} user(s). Assign your projects to help them contribute.
              </Typography>

              <Divider sx={{ mb: 2 }} />

              <List>
                {subordinates.map((sub) => (
                  <ListItem
                    key={sub.user_id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {sub.username}
                          </Typography>
                          <RoleBadge role={sub.role} size="small" />
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" component="span" color="text.secondary">
                            {sub.email}
                          </Typography>
                          <br />
                          <Chip
                            label={`${sub.project_count || 0} projects assigned`}
                            size="small"
                            variant="outlined"
                            sx={{ mt: 0.5 }}
                          />
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AssignmentIcon />}
                        onClick={() => handleAssignProjects(sub)}
                        disabled={myProjects.length === 0}
                      >
                        Assign Projects
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </CardContent>
      </Card>

      {/* Assignment Dialog */}
      {openAssignDialog && selectedSubordinate && (
        <SubordinateProjectAssignmentDialog
          open={openAssignDialog}
          onClose={handleDialogClose}
          subordinate={selectedSubordinate}
          myProjects={myProjects}
          onSuccess={(message) => setSuccess(message)}
          onError={(message) => setError(message)}
        />
      )}
    </Box>
  );
};

export default TeamManagement;
